import React, {
  TextareaHTMLAttributes,
  forwardRef,
  useEffect,
  useRef,
  useCallback,
} from "react";
import styles from "./MTextArea.module.css";
import labelStyles from "./MInputField.module.css";
import { Label } from "./label";
import { IconEdit } from "@tabler/icons-react";

export interface MTextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
  error?: boolean;
  success?: boolean;
  label?: string;
  labelClassName?: string;
  variant?: "drawerInput" | "SummaryDisplay";
  disableAutoScrollIntoView?: boolean;
  showEditIcon?: boolean;
  onEditIconClick?: () => void;
  autoGrow?: boolean;
}

// Detect platform once at module level
const isIOS =
  typeof navigator !== "undefined" &&
  /iPhone|iPad|iPod/i.test(navigator.userAgent);

const MTextArea = forwardRef<HTMLTextAreaElement, MTextAreaProps>(
  (
    {
      className = "",
      value,
      defaultValue,
      disabled,
      error,
      success,
      label,
      maxLength,
      labelClassName,
      variant,
      disableAutoScrollIntoView,
      showEditIcon,
      onEditIconClick,
      onFocus,
      autoGrow,
      ...props
    },
    ref
  ) => {
    // Determine if textarea is "entered" (has value and is not disabled)
    const isEntered =
      !disabled &&
      ((typeof value === "string" && value.length > 0) ||
        (typeof defaultValue === "string" && defaultValue.length > 0));
    let stateClass = "";
    if (error) stateClass = styles.error;
    else if (success) stateClass = styles.success;
    else if (isEntered) stateClass = styles.entered;
    const currentLength =
      typeof value === "string"
        ? value.length
        : typeof defaultValue === "string"
        ? defaultValue.length
        : 0;

    const internalRef = useRef<HTMLTextAreaElement>(null);
    const resolvedRef =
      (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

    const MIN_HEIGHT = 203;

    const adjustHeight = useCallback(() => {
      const el = resolvedRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.max(MIN_HEIGHT, el.scrollHeight)}px`;
    }, [resolvedRef]);

    useEffect(() => {
      if (autoGrow) adjustHeight();
    }, [autoGrow, value, adjustHeight]);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (!disableAutoScrollIntoView) {
        // Only apply scroll logic for iOS
        if (isIOS) {
          setTimeout(() => {
            const target = e.target;
            const rect = target.getBoundingClientRect();
            const viewportHeight =
              window.visualViewport?.height ?? window.innerHeight;
            const safeBottom = 24;
            const shouldScroll = rect.bottom > viewportHeight - safeBottom;
            if (shouldScroll) {
              target.scrollIntoView({ behavior: "smooth", block: "end" });
            }
          }, 300);
        }
      }
      // Call original onFocus if provided
      if (onFocus) {
        onFocus(e);
      }
    };

    return (
      <div className={styles.mTextAreaWrapper}>
        <div className={styles.labelRow}>
          <Label
            className={[
              labelStyles.minputfieldLabel,
              variant === "SummaryDisplay"
                ? labelStyles.summaryDisplayLabel
                : "",
              variant === "drawerInput" ? labelStyles.drawerLabel : "",
              variant === "drawerInput" ? "body-xs-medium" : "",
              labelClassName,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {label}
          </Label>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {showEditIcon && onEditIconClick && (
              <button
                type="button"
                onClick={onEditIconClick}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                aria-label="Edit details"
              >
                <IconEdit size={20} color="#45168C" />
              </button>
            )}
            {!showEditIcon && typeof maxLength === "number" && (
              <div className={styles.mTextAreaCharCount}>
                {currentLength > 0
                  ? `${maxLength - currentLength} characters left`
                  : `${maxLength} characters`}
              </div>
            )}
          </div>
        </div>
        <textarea
          ref={resolvedRef}
          className={[
            styles.mTextArea,
            stateClass,
            variant === "drawerInput" ? "body-base-medium" : "",
            className,
          ]
            .filter(Boolean)
            .join(" ")}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          maxLength={maxLength}
          onFocus={handleFocus}
          style={{
            color:
              variant === "drawerInput"
                ? "var(--colour-text-default, #2A2A2A)"
                : undefined,
            ...(autoGrow
              ? { minHeight: "203px", resize: "none", overflow: "hidden" }
              : {}),
          }}
          {...props}
        />
      </div>
    );
  }
);

MTextArea.displayName = "MTextArea";

export default MTextArea;
